import { AlertTriangle, Camera, Check, ClipboardCheck, CloudUpload, UserX } from 'lucide-react';
import { INSPECTION_STATUS_LABEL } from '@obraplan/core/domain/planning/daily-inspection';
import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { formatQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import { quantityBalance } from '@obraplan/core/domain/planning/measurement';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AvisoDaFila } from './AvisoDaFila';
import type { SyncDoCartao } from './fila-da-conferencia';

/**
 * O cartão da conferência, no formato do requisito.
 *
 * ```text
 * 17/08/2026
 * Funcionário: João da Silva
 * Função: Pedreiro
 * Planejado: Alvenaria — 3º pavimento
 * Horas: 9h
 * ```
 *
 * Desenhado para **tablet em pé, no canteiro**: um alvo grande abre o dia da pessoa, e
 * registrar ausência fica ao lado, menor, porque é a exceção.
 *
 * ## Um botão só para o dia (Fase 20)
 *
 * Confirmar e "medição e fotos" abriam a mesma caixa e pareciam duas coisas. Agora há um
 * botão: ele abre o dia da pessoa, e é lá dentro que as horas, a quantidade, a checklist e
 * as fotos moram — e é de lá que o dia se confirma. O cartão continua sendo o resumo do
 * que já está gravado, e é por ele que se reabre uma conferência encerrada (RN-106).
 */
/**
 * O que o cartão mostra da qualidade de uma frente (Fase 19).
 *
 * Só o resumo: o selo diz se a frente medida foi verificada e como; o diálogo de medição
 * é onde se responde.
 */
export interface FrontQualityBadge {
  /** A obra cadastrou serviço para a disciplina desta frente — sem isso, não há o que verificar. */
  readonly hasServices: boolean;
  readonly verified: boolean;
  readonly approved: number;
  readonly rejected: number;
  readonly total: number;
}

export interface InspectionCardProps {
  line: InspectionLine;
  canConfirm: boolean;
  canModify: boolean;
  disabled: boolean;
  /** Por `activityId`. Ausente = a qualidade ainda não foi lida; o cartão não diz nada. */
  quality?: ReadonlyMap<string, FrontQualityBadge>;
  onAbsence: () => void;
  onReopen: (inspectionId: string) => void;
  /**
   * Abre o dia da pessoa — horas, quantidade executada, qualidade e fotos na mesma caixa,
   * e o botão de confirmar lá dentro.
   *
   * A conferência é criada se ainda não existir (RN-100): medir e fotografar vêm antes do
   * veredito, e é o instante em que o segundo par de olhos começou a olhar.
   */
  onOpen: () => void;
  /**
   * O que este dia tem na fila do aparelho (Fase 43, RN-604, RN-608). O cartão já mostra o
   * efeito esperado; a marca diz que ele ainda não chegou ao servidor, ou que foi recusado.
   */
  sync?: SyncDoCartao;
}

/**
 * Planejado × executado de uma frente, em uma linha.
 *
 * Só aparece quando há o que dizer: frente sem meta e sem medição não ganha uma linha
 * vazia repetindo que não há nada. A diferença é escrita com sinal — `+2 m²` e `−2 m²`
 * são notícias diferentes, e a cor sozinha não diz qual é qual para quem não a enxerga.
 */
function MeasurementLine({
  planned,
  executed,
  unit,
}: {
  planned: number | undefined;
  executed: number | undefined;
  unit: UnitOfMeasure;
}) {
  if (planned === undefined && executed === undefined) return null;

  const balance = quantityBalance(planned, executed);
  const difference = balance.difference;

  return (
    <p className="text-xs text-(--color-content-muted)">
      {planned === undefined ? 'Sem meta' : `Meta ${formatQuantity(planned, unit)}`}
      {executed !== undefined && ` · Executado ${formatQuantity(executed, unit)}`}
      {difference !== undefined && difference !== 0 && (
        <span
          className={
            difference < 0 ? 'ml-1 text-(--color-state-warn)' : 'ml-1 text-(--color-state-ok)'
          }
        >
          ({difference > 0 ? '+' : '−'}
          {formatQuantity(Math.abs(difference), unit)})
        </span>
      )}
    </p>
  );
}

/**
 * O selo de qualidade da frente medida (RN-218).
 *
 * Só aparece quando há quantidade lançada e serviço cadastrado: frente sem serviço não
 * tem o que verificar, e dizer "sem verificação" nela seria cobrar o que não se pede.
 */
export function QualityBadge({ entry }: { entry: FrontQualityBadge | undefined }) {
  if (entry === undefined || !entry.hasServices) return null;
  if (!entry.verified) {
    return (
      <Badge tone="neutral" className="mt-1">
        Sem verificação
      </Badge>
    );
  }
  if (entry.rejected > 0) {
    return (
      <Badge tone="warn" className="mt-1" icon={<ClipboardCheck aria-hidden="true" />}>
        {entry.rejected === 1 ? '1 NC' : `${String(entry.rejected)} NC`}
      </Badge>
    );
  }
  return (
    <Badge tone="ok" className="mt-1" icon={<ClipboardCheck aria-hidden="true" />}>
      {entry.total === 0
        ? 'Serviço declarado'
        : `Qualidade ${String(entry.approved)}/${String(entry.total)}`}
    </Badge>
  );
}

const STATUS_TONE = {
  PENDING: 'neutral',
  CONFIRMED: 'ok',
  ADJUSTED: 'info',
  ABSENT: 'warn',
} as const;

export function InspectionCard({
  line,
  canConfirm,
  canModify,
  disabled,
  quality,
  onAbsence,
  onReopen,
  onOpen,
  sync,
}: InspectionCardProps) {
  const status = line.inspection?.status ?? 'PENDING';
  const closed = status !== 'PENDING';

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <header className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-lg font-medium">{line.resource.name}</p>
            <p className="text-sm text-(--color-content-muted)">
              {line.resource.detail}
              {line.resource.teamName !== undefined && ` · ${line.resource.teamName}`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {/* Lado a lado: empilhados, os dois selos desencontravam quando o cabeçalho quebrava. */}
            <div className="flex flex-wrap justify-end gap-1">
              <Badge tone={STATUS_TONE[status]}>{INSPECTION_STATUS_LABEL[status]}</Badge>
              {sync?.state === 'pending' && (
                <Badge tone="info" icon={<CloudUpload aria-hidden="true" />}>
                  {sync.count === 1 ? 'Aguardando envio' : `${String(sync.count)} aguardando envio`}
                </Badge>
              )}
            </div>
            {line.changeCount > 0 && (
              <span className="text-xs text-(--color-state-warn)">
                {line.changeCount === 1
                  ? '1 alteração hoje'
                  : `${String(line.changeCount)} alterações hoje`}
              </span>
            )}
          </div>
        </header>

        {line.allocations.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">Sem horas planejadas neste dia.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {line.allocations.map(({ activity, allocation, unit }) => (
              <li
                key={allocation.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">{activity.label}</p>
                  <p className="text-xs text-(--color-content-muted)">{activity.towerName}</p>
                  {unit !== undefined && (
                    <MeasurementLine
                      planned={allocation.plannedQuantity}
                      executed={allocation.executedQuantity}
                      unit={unit}
                    />
                  )}
                  {allocation.executedQuantity !== undefined && (
                    <QualityBadge entry={quality?.get(activity.activityId)} />
                  )}
                </div>
                <span className="tabular font-medium">
                  {Hours.ofQuarters(allocation.quarterHours).format()}
                </span>
              </li>
            ))}
          </ul>
        )}

        {sync?.state === 'rejected' && (
          <AvisoDaFila
            sync={sync}
            pendente=""
            recusado="O servidor não aceitou o que foi feito sem sinal"
          />
        )}

        {line.inspection?.absenceReason !== undefined && (
          <p className="flex items-start gap-2 text-sm text-(--color-state-warn)">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Ausente — {line.inspection.absenceReason}. As{' '}
            {Hours.ofQuarters(line.inspection.plannedQuarterHours).format()} planejadas permanecem
            registradas.
          </p>
        )}

        <footer className="flex flex-wrap items-center gap-2">
          {/*
            Um botão para o dia inteiro (Fase 20). Com a conferência aberta ele leva o nome
            do que vai acontecer lá dentro — confirmar as horas do dia, que é o desfecho
            comum; encerrada, leva o nome do que ainda se faz nela, porque medir e
            fotografar vêm depois do veredito tantas vezes quanto antes.
          */}
          {!closed && canConfirm ? (
            <>
              {/* Alvo grande: é a ação mais comum, feita com o dedo e o tablet na mão. */}
              <Button
                variant="primary"
                className="min-h-11 flex-1 basis-full sm:basis-auto"
                disabled={disabled}
                onClick={onOpen}
              >
                <Check aria-hidden="true" />
                Confirmar {Hours.ofQuarters(line.plannedQuarterHours).format()}
              </Button>
              <Button
                variant="secondary"
                className="min-h-11"
                disabled={disabled}
                onClick={onAbsence}
              >
                <UserX aria-hidden="true" />
                Ausente
              </Button>
            </>
          ) : (
            <Button variant="ghost" className="min-h-11 md:h-9" onClick={onOpen}>
              <Camera aria-hidden="true" />
              Medição e fotos
            </Button>
          )}

          {closed && canModify && (
            <Button
              variant="secondary"
              className="min-h-11 md:h-9"
              onClick={() => line.inspection !== undefined && onReopen(line.inspection.id)}
            >
              Reabrir
            </Button>
          )}
        </footer>
      </CardContent>
    </Card>
  );
}
